import React from 'react';

/**
 * Very basic markdown to simple React elements parser.
 * This is a temporary solution until a more robust library is integrated.
 * 
 * @param {string} text - The markdown text to parse
 * @param {string} basePath - Optional base path for relative URLs
 * @returns {Array} - Array of React elements
 */
export const renderMarkdown = (text, basePath = '') => {
    if (!text) return null;

    // Handle string content that might be HTML (defensive check)
    if (text.trim().startsWith('<!DOCTYPE html>') || text.trim().startsWith('<html')) {
        return (
            <div className="html-content-warning">
                <p>⚠️ Content could not be loaded as a guide. It appears to be a system page.</p>
                <div className="raw-html-preview" style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                    {text.substring(0, 500)}...
                </div>
            </div>
        );
    }

    const lines = text.split('\n');
    const elements = [];
    let currentList = [];
    let listType = null; // 'ul' or 'ol'

    const flushList = () => {
        if (currentList.length > 0) {
            const listKey = `list-${elements.length}`;
            if (listType === 'ul') {
                elements.push(<ul key={listKey}>{currentList}</ul>);
            } else {
                elements.push(<ol key={listKey}>{currentList}</ol>);
            }
            currentList = [];
            listType = null;
        }
    };

    lines.forEach((line, i) => {
        // Headers
        if (line.startsWith('# ')) {
            flushList();
            elements.push(<h1 key={i}>{line.substring(2)}</h1>);
            return;
        }
        if (line.startsWith('## ')) {
            flushList();
            elements.push(<h2 key={i}>{line.substring(3)}</h2>);
            return;
        }
        if (line.startsWith('### ')) {
            flushList();
            elements.push(<h3 key={i}>{line.substring(4)}</h3>);
            return;
        }

        // Horizontal Rule
        if (line.trim() === '---' || line.trim() === '***') {
            flushList();
            elements.push(<hr key={i} />);
            return;
        }

        // Lists
        const ulMatch = line.match(/^[\s]*[-*+]\s+(.*)/);
        const olMatch = line.match(/^[\s]*\d+\.\s+(.*)/);

        if (ulMatch) {
            if (listType !== 'ul') flushList();
            listType = 'ul';
            currentList.push(<li key={`li-${i}`}>{formatInline(ulMatch[1], basePath)}</li>);
            return;
        }

        if (olMatch) {
            if (listType !== 'ol') flushList();
            listType = 'ol';
            currentList.push(<li key={`li-${i}`}>{formatInline(olMatch[1], basePath)}</li>);
            return;
        }

        // If not a list item but we were in a list, flush it
        if (line.trim() !== '') {
            flushList();
        }

        // Empty lines
        if (line.trim() === '') {
            flushList();
            elements.push(<br key={i} />);
            return;
        }

        // Regular paragraph
        elements.push(<p key={i} className="md-text">{formatInline(line, basePath)}</p>);
    });

    flushList();
    return elements;
};

/**
 * Formats inline markdown elements like bold, italic, and links/images.
 */
function formatInline(text, basePath = '') {
    if (!text) return text;

    // This is a very simplified inline parser
    // Handle images first to avoid conflict with link parsing: ![alt](url)
    const imageRegex = /!\[(.*?)\]\((.*?)\)/g;

    // We'll use a placeholder/replacement strategy for complex nested parsing
    let elements = [];
    let lastIndex = 0;
    let match;

    // Re-assignable string for iterative processing
    let currentText = text;

    // Handle Images and Links
    // Note: This is still a basic parser and might struggle with extremely complex nesting

    // First, let's handle bold (**text**) properly with a split
    let parts = currentText.split(/(\*\*.*?\*\*)/g);

    return parts.flatMap((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return [<strong key={`bold-${i}`}>{part.substring(2, part.length - 2)}</strong>];
        }

        // Process images and links in the remaining text
        const subElements = [];
        let subIndex = 0;

        // Regex for both images and links
        // Group 1: ! (optional), Group 2: label, Group 3: url
        const linkImgRegex = /(!)?\[(.*?)\]\((.*?)\)/g;
        let subMatch;

        while ((subMatch = linkImgRegex.exec(part)) !== null) {
            // Text before the match
            if (subMatch.index > subIndex) {
                subElements.push(part.substring(subIndex, subMatch.index));
            }

            const isImage = subMatch[1] === '!';
            const label = subMatch[2];
            const url = subMatch[3];

            // Resolve relative URL
            let resolvedUrl = url;
            if (basePath && url.startsWith('./')) {
                resolvedUrl = basePath + (basePath.endsWith('/') ? '' : '/') + url.substring(2);
            } else if (basePath && !url.includes('://') && !url.startsWith('/')) {
                resolvedUrl = basePath + (basePath.endsWith('/') ? '' : '/') + url;
            }

            if (isImage) {
                subElements.push(
                    <div key={`img-${i}-${subMatch.index}`} className="md-image-container">
                        <img
                            src={resolvedUrl}
                            alt={label}
                            className="md-image"
                            style={{ maxWidth: '100%', borderRadius: '8px', marginTop: '8px', marginBottom: '8px', border: '1px solid rgba(255,255,255,0.1)' }}
                        />
                        {label && <span className="md-image-caption" style={{ display: 'block', fontSize: '11px', color: '#888', textAlign: 'center', marginTop: '-4px', marginBottom: '12px' }}>{label}</span>}
                    </div>
                );
            } else {
                subElements.push(
                    <a key={`link-${i}-${subMatch.index}`} href={resolvedUrl} target="_blank" rel="noopener noreferrer">
                        {label}
                    </a>
                );
            }
            subIndex = linkImgRegex.lastIndex;
        }

        // Remaining text
        if (subIndex < part.length) {
            subElements.push(part.substring(subIndex));
        }

        return subElements;
    });
}
